import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import styles from "./E3File_EC.module.css";
import E3Button from "./E3Button_EC";
import { FileDrop } from "react-file-drop";
import useFetch from "e3-common/hooks/useFetch";

/**
 * E3FileMulti
 * @type React.FC<E3FileMultiPropTypes>
 */
const E3FileMulti = React.forwardRef((props, ref) => {
  Object.keys(props).forEach((p) => {
    if (!Object.keys(E3FileMultiPropTypes).includes(p)) {
      console.error(`E3FileMulti: 정의되지 않은 prop ${p}가 전달되었습니다.`);
    }
  });

  /**
   * E3FileMulti Component의 State
   */
  const [currState, setCurrState] = useState({
    readOnly: props.readOnly,
    attachSeq: null,
    filesInDB: [],
    filesToUpload: [],
    saveInDB: props.saveInDB,
  });

  /** 외부에서 접근할 수 있는 함수들 */
  React.useImperativeHandle(ref, () => ({
    /** 파일 목록 조회 */
    loadFiles(attachSeq) {
      _loadFileListByAttachSeq(attachSeq);
    },
    setReadOnly(b) {
      setCurrState((prev) => ({ ...prev, readOnly: b }));
    },
    /** 파일 저장 */
    async saveFiles() {
      /* ATTACH_SEQ가 존재하지않고, 업로드할 파일이 없을 때 => 신규 + 파일업로드 X => 서비스 호출 X */
      if (!currState.attachSeq && currState.filesToUpload.length <= 0) return null;

      const filesToDelete = currState.attachSeq
        ? currState.filesInDB.filter((f) => f.delete).map((f) => f.ATTACH_FILE_SEQ)
        : [];

      /* ATTACH_SEQ가 존재하고, 삭제된 파일, 추가된 파일이 없을 때 => 수정사항 없음 => 서비스 호출 X */
      if (currState.attachSeq && filesToDelete.length <= 0 && currState.filesToUpload.length <= 0) {
        return currState.attachSeq;
      }

      const lastOrd = currState.filesInDB.length > 0 ? currState.filesInDB[currState.filesInDB.length - 1].ORD : 0;

      const param = {
        ATTACH_SEQ: currState.attachSeq || "",
        FILES_TO_DELETE: JSON.stringify(filesToDelete),
        LAST_ORD: lastOrd,
        SAVE_IN_DB: currState.saveInDB,
      };
      const response = await request("/file-upload", {
        body: param,
        attachFiles: currState.filesToUpload,
      });

      if (response != null) {
        return response.ATTACH_SEQ;
      }
    },
    async saveOcrFiles() {
      if (currState.filesToUpload.length <= 0) return null;
      try {
        const response = await ocrRequest({
          attachFiles: currState.filesToUpload,
        });
        return response;
      } catch (error) {
        // 네트워크 오류 등 예외 처리
        console.log(error);
      }
    },

    getAttachSeq() {
      return currState.attachSeq;
    },

    /** 초기화 */
    clear() {
      setCurrState({
        readOnly: props.readOnly,
        attachSeq: null,
        filesInDB: [],
        filesToUpload: [],
      });
    },
  }));

  const { request, downloadFile, ocrRequest } = useFetch();
  const refFileInput = useRef();

  /**
   * 파일 추가
   * @param {*} files input으로부터 선택된 file 목록
   */
  const _onFileAdd = (files) => {
    setCurrState((prev) => ({
      ...prev,
      filesToUpload: [...prev.filesToUpload, ...files],
    }));
  };

  /**
   * 업로드 대상 파일 목록에서 제거
   * @param {*} fileToRemove
   */
  const _removeFromFilesToUpload = (fileToRemove) => {
    const remainedFiles = currState.filesToUpload.filter((f) => f !== fileToRemove);
    setCurrState((prev) => ({
      ...prev,
      filesToUpload: remainedFiles,
    }));
  };

  /**
   * 이미 저장되어 있는 파일 목록에서 삭제대상으로 Flag 처리
   * @param {*} fileToRemove
   * @param {*} cancel
   */
  const _removeFromFilesInDB = (fileToRemove, cancel = false) => {
    const files = currState.filesInDB.map((f) => ({
      ...f,
      delete: f !== fileToRemove ? f.delete : f === fileToRemove && !cancel,
    }));

    setCurrState((prev) => ({
      ...prev,
      filesInDB: files,
    }));
  };

  /**
   * 파일 목록 조회
   * @param {*} attachSeq
   */
  const _loadFileListByAttachSeq = async (attachSeq) => {
    const param = {
      ATTACH_SEQ: attachSeq,
    };
    const response = await request("/file-list", {
      body: param,
    });

    const files = response ?? [];

    setCurrState((prev) => ({
      ...prev,
      attachSeq: attachSeq,
      filesInDB: files,
      filesToUpload: [],
    }));
  };

  return (
    <div className={styles.e3File}>
      {!currState.readOnly ? (
        <>
          <input
            style={{ display: "none" }}
            id="contentList"
            ref={refFileInput}
            type="file"
            multiple
            onChange={(e) => _onFileAdd(e.target.files)}
          />
          <div className={styles.fileSelect}>
            <FileDrop
              className={styles.fileDropArea}
              onDrop={(files) => {
                _onFileAdd(files);
              }}
              onTargetClick={() => {
                refFileInput.current?.click();
              }}
            >
              <div className={styles.box + " " + styles.file_upload}>
                <div className={styles.thumb}>
                  <img src={import.meta.env.VITE_PUBLIC_URL + "/assets/images/icon/icon_upload_white.svg"} />
                </div>
                <p style={{ marginTop: "5px" }}>파일을 여기에 드래그 하거나, 클릭하여 파일을 업로드하세요.</p>
              </div>
              {/* <E3Button label={'파일선택'} colorType="main-outline" onClick={() => {}} /> */}
            </FileDrop>
          </div>
        </>
      ) : null}

      <div className={styles.listSection}>
        <ul
          style={{
            display: "flex",
            flexWrap: "wrap",
            flexDirection: props.fileListDirection,
          }}
        >
          {currState.filesInDB.map((file, i) => (
            <li key={i} className={styles.fileRow}>
              <span className={`${styles.fileName} ${file.delete ? styles.lineThrough : ""}`}>{file.ORI_FILE_NM}</span>
              {file.delete ? null : (
                <E3Button
                  label="다운로드"
                  colorType="sub2"
                  iconType={"down"}
                  size={"small"}
                  onClick={() => {
                    downloadFile(file.ATTACH_FILE_SEQ, currState.saveInDB);
                  }}
                />
              )}

              {!currState.readOnly ? (
                file.delete ? (
                  <E3Button
                    key={"cancel"}
                    label="삭제취소"
                    colorType="sub2"
                    iconType={"del"}
                    size={"small"}
                    onClick={() => {
                      _removeFromFilesInDB(file, true);
                    }}
                  />
                ) : (
                  <E3Button
                    key={"delete"}
                    label="삭제"
                    iconType={"del"}
                    size={"small"}
                    onClick={() => {
                      _removeFromFilesInDB(file);
                    }}
                  />
                )
              ) : null}
            </li>
          ))}
          {currState.filesToUpload.map((file, i) => (
            <li key={i} className={styles.fileRow}>
              <span className={styles.fileName}>{file.name}</span>
              <E3Button
                label="삭제"
                iconType={"del"}
                size={"small"}
                onClick={() => {
                  _removeFromFilesToUpload(file);
                }}
              />
            </li>
          ))}
        </ul>
      </div>
      {props.remark ? <div className={styles.description}> {props.remark} </div> : null}
    </div>
  );
});

const E3FileMultiPropTypes = {
  readOnly: PropTypes.bool,
  remark: PropTypes.node,
  saveInDB: PropTypes.bool,
  fileListDirection: PropTypes.string, // row, column
};

E3FileMulti.propTypes = E3FileMultiPropTypes;
E3FileMulti.defaultProps = {
  readOnly: false,
  saveInDB: false,
  fileListDirection: "row",
};
E3FileMulti.displayName = "E3FileMulti";
export default E3FileMulti;
